import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrackChanges as TrackChangesIcon,
} from '@mui/icons-material';
import { releaseNotes } from './ReleaseNotes';
import dayjs from 'dayjs';

// Types for the release notes data
interface ReleaseNote {
  version: string;
  releaseDate: string;
  notes: string[];
}

interface ReleaseModalProps {
  open: boolean;
  onClose: () => void;
  isSaving?: boolean;
  savingError?: string;
}

export default function ReleaseModal({
  open,
  onClose,
  isSaving = false,
  savingError,
}: ReleaseModalProps) {
  const selectedVersion: ReleaseNote = releaseNotes[0];

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return dayjs(date).format('MMMM D, YYYY');
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      {/* Header with close button */}
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'primary.main',
          color: 'white',
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="div"
            fontWeight="bold"
            color="#fff"
          >
            Release {selectedVersion.version}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ opacity: 0.9, mt: 0.5 }}
            color="#fff"
          >
            Released on {formatDate(selectedVersion.releaseDate)}
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Version badge */}
        <Box sx={{ mt: 1, mb: 1, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={`v${selectedVersion.version}`}
            color="primary"
            variant="outlined"
            size="medium"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderWidth: 2,
            }}
          />
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Release notes list */}
        <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
          What's New
        </Typography>

        <List sx={{ pt: 0 }}>
          {selectedVersion.notes.map((note, index) => (
            <ListItem
              key={index}
              sx={{
                px: 0,
                py: 1,
                '&:not(:last-child)': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <TrackChangesIcon color="primary" sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.4,
                      color: 'text.primary',
                    }}
                  >
                    {note}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        {/* Error message */}
        {savingError && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error.dark">
              {savingError}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={isSaving}
          variant="contained"
          size="large"
          sx={{
            minWidth: 120,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
          }}
        >
          {isSaving ? 'Closing...' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
